/**
 * Pure selection logic shared by list/grid multi-select UIs (shift-click range,
 * ctrl/cmd-click toggle, plain click = select-one). Kept framework-free so it
 * can be unit-tested directly; useRangeSelection wraps it with React state.
 */

/**
 * Apply a click to the current selection.
 *
 * @param {{selected: Set<string>, lastIndex: number|null}} state
 * @param {number} index - index of the clicked item within orderedIds
 * @param {string[]} orderedIds - the full ordered id list (for range fills)
 * @param {{shift?: boolean, ctrlOrMeta?: boolean}} modifiers
 * @returns {{selected: Set<string>, lastIndex: number}}
 */
export const applySelectionClick = (state, index, orderedIds, modifiers = {}) => {
  const selected = state?.selected instanceof Set ? state.selected : new Set();
  const lastIndex = state?.lastIndex ?? null;
  const ids = Array.isArray(orderedIds) ? orderedIds : [];
  const id = ids[index];

  // Out-of-range click — no change.
  if (id == null) return { selected: new Set(selected), lastIndex };

  // Shift-click: select the inclusive range from the last anchor to here.
  if (modifiers.shift && lastIndex != null && ids[lastIndex] != null) {
    const start = Math.min(lastIndex, index);
    const end = Math.max(lastIndex, index);
    const next = new Set(selected);
    for (let i = start; i <= end; i++) {
      if (ids[i] != null) next.add(ids[i]);
    }
    return { selected: next, lastIndex };
  }

  // Ctrl/Cmd-click: toggle just this item, move the anchor here.
  if (modifiers.ctrlOrMeta) {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    return { selected: next, lastIndex: index };
  }

  // Plain click: select only this item.
  return { selected: new Set([id]), lastIndex: index };
};

/**
 * Group-scoped variant used for observation-row selection in the file browser.
 *
 * Selection is global (ctrl/cmd-click can accumulate rows across different
 * groups so one "Delete selected" action can span them), but shift-click
 * *ranges* are confined to the group that owns the anchor: a shift-click in a
 * different group than the anchor is treated as a fresh select-one rather than
 * filling across the boundary. The anchor therefore remembers its containerId.
 *
 * @param {{selected: Set<string>, anchor: {containerId: string, index: number}|null}} state
 * @param {string} containerId - id of the group the clicked row lives in
 * @param {number} index - index of the clicked row within orderedKeys (that group's rows)
 * @param {string[]} orderedKeys - the group's ordered selectable row keys (for range fills)
 * @param {{shift?: boolean, ctrlOrMeta?: boolean}} modifiers
 * @returns {{selected: Set<string>, anchor: {containerId: string, index: number}|null}}
 */
export const applyGroupedSelectionClick = (state, containerId, index, orderedKeys, modifiers = {}) => {
  const selected = state?.selected instanceof Set ? state.selected : new Set();
  const anchor = state?.anchor || null;
  const sameContainer = !!anchor && anchor.containerId === containerId;
  // Only honour shift as a range when the anchor is in this same group.
  const lastIndex = modifiers.shift && sameContainer ? anchor.index : null;

  const inner = applySelectionClick({ selected, lastIndex }, index, orderedKeys, {
    shift: lastIndex != null,
    ctrlOrMeta: !!modifiers.ctrlOrMeta,
  });

  // A range fill keeps the existing anchor; a toggle/select-one re-anchors here.
  const nextAnchor = lastIndex != null ? anchor : { containerId, index };
  return { selected: inner.selected, anchor: nextAnchor };
};
